import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Sparkles, Check, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { ArtThumb } from "@/components/riptag/TrackTable";

const SOURCE_CHIP = {
  "file tag": "bg-emerald-500/15 text-emerald-300",
  musicbrainz: "bg-cyan-500/15 text-cyan-300",
  ai: "bg-amber-500/15 text-amber-300",
};

export const GenreSuggestModal = ({ onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState(null);
  const [allowed, setAllowed] = useState([]);
  const [useAi, setUseAi] = useState(true);
  const [busy, setBusy] = useState("");
  const [edits, setEdits] = useState({});

  const loadPending = () => api.get("/music/genres/suggestions").then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  const loadPreview = () => api.get("/music/genres/suggest/preview").then((r) => setPreview(r.data)).catch(() => {});
  useEffect(() => {
    loadPreview();
    api.get("/music/genres").then((r) => setAllowed((r.data.genres || []).map((g) => g.name))).catch(() => {});
    loadPending();
  }, []);

  const generate = async () => {
    setBusy("gen");
    try {
      const r = await api.post("/music/genres/suggest", { use_ai: useAi });
      const n = r.data.suggestions.length;
      const skipped = r.data.no_evidence || 0;
      toast.success(`${n} suggestion${n === 1 ? "" : "s"} ready${r.data.charged ? ` — ${r.data.charged} credit${r.data.charged === 1 ? "" : "s"} used for AI` : " — no credits charged"}${skipped ? ` · ${skipped} track${skipped === 1 ? "" : "s"} had too little evidence` : ""}`);
      loadPending();
      loadPreview();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suggestion run failed"); }
    setBusy("");
  };

  const apply = async (t) => {
    setBusy(t.track_id);
    try {
      const primary = edits[t.track_id] ?? t.genre_suggestion?.primary;
      await api.post(`/music/genres/suggestions/${t.track_id}/apply`, { primary });
      setRows((p) => p.filter((r) => r.track_id !== t.track_id));
      toast.success(`"${t.title}" → ${primary}`);
      loadPreview();
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    setBusy("");
  };

  const reject = async (t) => {
    setBusy(t.track_id);
    try {
      await api.post(`/music/genres/suggestions/${t.track_id}/reject`);
      setRows((p) => p.filter((r) => r.track_id !== t.track_id));
      loadPreview();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    setBusy("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3" onClick={() => !busy && onClose()}
      data-testid="ml-genre-suggest-modal">
      <div className="w-full max-w-3xl rounded-xl border border-line bg-bg-surface p-4 sm:p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="type-section-heading text-ink-primary flex items-center gap-2">
            <Sparkles size={15} className="text-brand-amber" /> AI Genre Suggestions
          </h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-primary" data-testid="ml-genre-suggest-close"><X size={15} /></button>
        </div>
        <p className="type-body text-ink-muted mb-3">
          Evidence order: embedded file tags (free) → MusicBrainz community genres (free) → AI classification only when
          both are empty. Genre can't always be read from audio alone — every suggestion shows its evidence and waits
          for your approval. Songs can hold several genres.
        </p>

        {preview && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-bg-base px-3 py-2 mb-3" data-testid="ml-genre-suggest-preview">
            <span className="text-xs text-ink-secondary">
              <b className="text-brand-amber">{preview.missing_genre ?? 0}</b> track{(preview.missing_genre ?? 0) === 1 ? "" : "s"} missing a genre ·{" "}
              <b>{preview.ai_candidates ?? 0}</b> may need AI
              {preview.free_for_admin
                ? " · admin — AI free (usage tracked)"
                : ` · est. ${preview.est_credits ?? 0} credit${(preview.est_credits ?? 0) === 1 ? "" : "s"} (${preview.cost_per_10 ?? 1}/10 tracks)`}
            </span>
            <label className="flex items-center gap-1.5 text-xs text-ink-secondary cursor-pointer ml-auto">
              <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)}
                data-testid="ml-genre-suggest-ai-toggle" className="accent-amber-500" /> Use AI fallback
            </label>
            <button onClick={generate} disabled={!!busy || !(preview.missing_genre > 0)} data-testid="ml-genre-suggest-run"
              className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-1.5 text-bg-base disabled:opacity-50">
              {busy === "gen" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Generate suggestions
            </button>
          </div>
        )}

        {rows === null ? <Loader2 size={16} className="animate-spin text-ink-muted" /> : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line p-6 text-center type-body text-ink-muted" data-testid="ml-genre-suggest-empty">
            No pending suggestions — generate some above, or everything already has a genre.
          </div>
        ) : (
          <div className="rounded-lg border border-line divide-y divide-line" data-testid="ml-genre-suggest-list">
            {rows.map((t) => {
              const s = t.genre_suggestion || {};
              return (
                <div key={t.track_id} className="flex flex-wrap items-center gap-2.5 px-3 py-2" data-testid={`ml-genre-suggest-row-${t.track_id}`}>
                  <ArtThumb url={t.art_url} size="w-9 h-9" />
                  <div className="min-w-[140px] flex-1">
                    <div className="type-list-title text-ink-primary truncate">{t.title || t.filename}</div>
                    <div className="type-list-subtitle text-ink-muted truncate">
                      {t.artist || "Unknown"} · current: {t.genre || "—"}
                    </div>
                    <div className="text-[10px] text-ink-muted italic truncate">{(s.evidence || []).join(" · ")}</div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-mono font-bold ${SOURCE_CHIP[s.source] || "bg-bg-elevated text-ink-muted"}`}>
                    {s.label || s.source}
                  </span>
                  <select value={edits[t.track_id] ?? s.primary ?? ""} data-testid={`ml-genre-suggest-primary-${t.track_id}`}
                    onChange={(e) => setEdits((p) => ({ ...p, [t.track_id]: e.target.value }))}
                    className="shrink-0 rounded-md border border-line bg-bg-base px-2 py-1 text-xs max-w-40">
                    {[...new Set([s.primary, ...allowed])].filter(Boolean).map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                  {(s.secondary || []).length > 0 && (
                    <span className="shrink-0 text-[10px] text-ink-muted">+ {(s.secondary || []).join(", ")}</span>
                  )}
                  <button onClick={() => apply(t)} disabled={!!busy} data-testid={`ml-genre-suggest-apply-${t.track_id}`}
                    className="shrink-0 flex items-center gap-1 rounded-full bg-emerald-500/90 text-black px-2.5 py-1 text-[10px] font-bold hover:opacity-90 disabled:opacity-50">
                    {busy === t.track_id ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Apply
                  </button>
                  <button onClick={() => reject(t)} disabled={!!busy} data-testid={`ml-genre-suggest-reject-${t.track_id}`}
                    className="shrink-0 flex items-center gap-1 rounded-full border border-line px-2.5 py-1 text-[10px] font-bold text-ink-muted hover:text-rose-300 disabled:opacity-50">
                    <XCircle size={10} /> Reject
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
